import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tasksApi } from '../../../api/tasks.api'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Field } from '../../../components/ui/Field'
import { useAuth } from '../../../auth/AuthContext'
import { toast } from '../../../store/toastStore'
import { ApiError } from '../../../api/client'

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '–'
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}min` : `${m}min`
}

export function TaskTimeTracking({ taskId }: { taskId: string }) {
  const { me } = useAuth()
  const queryClient = useQueryClient()
  const [manualStart, setManualStart] = useState('')
  const [manualEnd, setManualEnd] = useState('')

  const entriesQuery = useQuery({ queryKey: ['tasks', taskId, 'time'], queryFn: async () => (await tasksApi.listTimeEntries(taskId)).data })
  const activeQuery = useQuery({ queryKey: ['tasks', 'time', 'active'], queryFn: async () => (await tasksApi.getActiveTimer()).data, refetchInterval: 30_000 })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'time'] })
    queryClient.invalidateQueries({ queryKey: ['tasks', 'time', 'active'] })
  }

  const startMutation = useMutation({
    mutationFn: () => tasksApi.startTimer(taskId),
    onSuccess: invalidate,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Timer konnte nicht gestartet werden.'),
  })

  const stopMutation = useMutation({
    mutationFn: () => tasksApi.stopTimer(taskId),
    onSuccess: invalidate,
  })

  const manualMutation = useMutation({
    mutationFn: () => tasksApi.addManualTime(taskId, { startedAt: manualStart, endedAt: manualEnd }),
    onSuccess: () => {
      setManualStart('')
      setManualEnd('')
      invalidate()
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Eintrag konnte nicht gespeichert werden.'),
  })

  const removeMutation = useMutation({
    mutationFn: (entryId: string) => tasksApi.removeTimeEntry(taskId, entryId),
    onSuccess: invalidate,
  })

  const isRunningHere = activeQuery.data?.taskId === taskId
  const totalSeconds = entriesQuery.data?.reduce((sum, e) => sum + (e.durationSeconds ?? 0), 0) ?? 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        {isRunningHere ? (
          <Button variant="danger" size="sm" loading={stopMutation.isPending} onClick={() => stopMutation.mutate()}>
            ⏸ Timer stoppen
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            loading={startMutation.isPending}
            disabled={Boolean(activeQuery.data) && !isRunningHere}
            onClick={() => startMutation.mutate()}
          >
            ▶ Timer starten
          </Button>
        )}
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Gesamt erfasst: {formatDuration(totalSeconds)}</span>
      </div>
      {activeQuery.data && !isRunningHere ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {activeQuery.data.task ? `Läuft gerade für "${activeQuery.data.task.title}"` : 'Läuft gerade als allgemeiner Projekt-Timer'} — erst dort stoppen.
        </span>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Manuell: Start" htmlFor="time-start">
          <Input id="time-start" type="datetime-local" value={manualStart} onChange={(e) => setManualStart(e.target.value)} />
        </Field>
        <Field label="Ende" htmlFor="time-end">
          <Input id="time-end" type="datetime-local" value={manualEnd} onChange={(e) => setManualEnd(e.target.value)} />
        </Field>
        <Button size="sm" disabled={!manualStart || !manualEnd} loading={manualMutation.isPending} onClick={() => manualMutation.mutate()}>
          Eintragen
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {entriesQuery.data?.map((entry) => (
          <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
            <span>
              {entry.user.displayName} · {new Date(entry.startedAt).toLocaleDateString('de-DE')} · {formatDuration(entry.durationSeconds)}
              {entry.source === 'MANUAL' ? ' (manuell)' : ''}
            </span>
            {entry.userId === me?.user.id ? (
              <button
                onClick={() => removeMutation.mutate(entry.id)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}
              >
                Entfernen
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}
